import classNames from "classnames";
import React from "react";
import "./kuika-skeleton.scss";

/**
 * The designer's ONE loading-placeholder primitive (KOA-15840 design review #8: the
 * workflow surfaces had three competing loading patterns — two spinner components and a
 * bare "Loading…" text). A skeleton previews the shape of what is coming, so a slow load
 * reads as progress instead of a hang. Reach for a composition first — `KuikaSkeletonList`,
 * `KuikaSkeletonCardGrid`, `KuikaSkeletonForm`, `KuikaSkeletonDetail`, `KuikaSkeletonStats`,
 * `KuikaSkeletonMenu`, `KuikaSkeletonTable` (a KuikaTable draws its own rows) — and compose
 * bars freely only for a shape none of them covers.
 *
 * Use it only where the shape is known and the content comes over the network, and only while
 * the region has none of its own data yet (`useLoadingPresentation`, first-load-skeletons.ts).
 *
 * Shimmer is decorative: it turns into a still pulse under `prefers-reduced-motion`, and the
 * block carries `aria-hidden` — the OWNER announces loading (`KuikaSkeletonRegion`), not the
 * placeholder.
 */
export interface IKuikaSkeletonProps {
  /** CSS size; numbers are px. Defaults: 100% × 14px (one text-ish bar). */
  width?: string | number;
  height?: string | number;
  className?: string;
}

const toCssSize = (value: string | number | undefined, fallback: string): string => {
  if (value === undefined) return fallback;
  return typeof value === "number" ? `${value}px` : value;
};

const KuikaSkeleton = ({ width, height, className }: IKuikaSkeletonProps): JSX.Element => (
  <span
    className={classNames("kuika-skeleton", className)}
    style={{ width: toCssSize(width, "100%"), height: toCssSize(height, "14px") }}
    aria-hidden="true"
    data-testid="kuikaSkeleton"
  />
);

export interface IKuikaSkeletonTableProps {
  /** Body row count. */
  rows?: number;
  rowHeight?: number;
  /** Prepends a narrower title-ish bar above the rows. */
  header?: boolean;
  className?: string;
}

export const KuikaSkeletonTable = ({
  rows = 4,
  rowHeight = 34,
  header = false,
  className
}: IKuikaSkeletonTableProps): JSX.Element => (
  <div className={classNames("kuika-skeleton-table", className)} aria-hidden="true" data-testid="kuikaSkeletonTable">
    {header && <KuikaSkeleton width="40%" height={14} />}
    {Array.from({ length: rows }, (_unused: unknown, index: number) => (
      <KuikaSkeleton key={index} height={rowHeight} />
    ))}
  </div>
);

// ── Owner ───────────────────────────────────────────────────────────────────────────────────

export interface IKuikaSkeletonRegionProps {
  /** What assistive tech hears while the region loads. Not shown. Ignored when `announce` is off. */
  label?: string;
  /**
   * Whether this region tells assistive tech it is loading (`aria-busy` and a `role="status"` line).
   * On by default. Turn it off on the second of two regions loading side by side — a list and its
   * detail panel — so the page says "Loading" once, from the first; the shapes stay hidden from
   * assistive tech either way.
   */
  announce?: boolean;
  className?: string;
  children: React.ReactNode;
  "data-testid"?: string;
}

/**
 * The owner every skeleton needs: `aria-busy` on the region, a `role="status"` line assistive tech
 * reads, and the shapes hidden from it. The status sits beside the shapes, not on the busy region
 * itself, because a busy live region holds its announcements back. With `announce={false}` it is
 * only the hidden shapes.
 */
export const KuikaSkeletonRegion = ({
  label = "Loading",
  announce = true,
  className,
  children,
  "data-testid": dataTestId = "kuikaSkeletonRegion"
}: IKuikaSkeletonRegionProps): JSX.Element => (
  <div
    className={classNames("kuika-skeleton-region", className)}
    aria-busy={announce ? "true" : undefined}
    data-testid={dataTestId}
  >
    {announce && (
      <span className="kuika-visually-hidden" role="status">
        {label}
      </span>
    )}
    <div className="kuika-skeleton-region__shapes" aria-hidden="true">
      {children}
    </div>
  </div>
);

// ── Shapes ──────────────────────────────────────────────────────────────────────────────────
// Each composition is bare and `aria-hidden` (wrap it in KuikaSkeletonRegion) and stands at the
// height of the content it previews — row heights from --row-h-*, fields from --control-h-* — so
// nothing jumps when the content arrives.

export type KuikaSkeletonDensity = "compact" | "default" | "comfortable";

/** Text bars that don't all end at the same place, so a list reads as text and not as stripes. */
const TEXT_BAR_WIDTHS: readonly string[] = ["72%", "56%", "64%", "48%", "60%"];
const TEXT_BAR_HEIGHT: string = "var(--gap-m)";
const SECOND_LINE_HEIGHT: string = "var(--gap-s)";

const textBarWidth = (index: number): string => TEXT_BAR_WIDTHS[index % TEXT_BAR_WIDTHS.length];

const indexes = (count: number): number[] => Array.from({ length: count }, (_unused: unknown, index: number) => index);

export interface IKuikaSkeletonListProps {
  rows?: number;
  density?: KuikaSkeletonDensity;
  /** A leading icon square on every row. */
  withIcon?: boolean;
  /** One text line, or a title and a narrower second line. */
  lines?: 1 | 2;
  className?: string;
}

/** A list row: optional icon, one or two lines of text. Rows are --row-h-{density} tall. */
export const KuikaSkeletonList = ({
  rows = 5,
  density = "default",
  withIcon = true,
  lines = 1,
  className
}: IKuikaSkeletonListProps): JSX.Element => (
  <div
    className={classNames("kuika-skeleton-list", `kuika-skeleton-list--${density}`, className)}
    aria-hidden="true"
    data-testid="kuikaSkeletonList"
  >
    {indexes(rows).map((index: number): JSX.Element => (
      <div key={index} className="kuika-skeleton-list__row">
        {withIcon && <KuikaSkeleton width="var(--control-icon-md)" height="var(--control-icon-md)" />}
        <div className="kuika-skeleton-list__text">
          <KuikaSkeleton width={textBarWidth(index)} height={TEXT_BAR_HEIGHT} />
          {lines === 2 && <KuikaSkeleton width="40%" height={SECOND_LINE_HEIGHT} />}
        </div>
      </div>
    ))}
  </div>
);

export interface IKuikaSkeletonCardGridProps {
  count?: number;
  className?: string;
}

/** A grid of cards (icon, title, two lines), filling the row the way the real card grid does. */
export const KuikaSkeletonCardGrid = ({ count = 6, className }: IKuikaSkeletonCardGridProps): JSX.Element => (
  <div
    className={classNames("kuika-skeleton-card-grid", className)}
    aria-hidden="true"
    data-testid="kuikaSkeletonCardGrid"
  >
    {indexes(count).map((index: number): JSX.Element => (
      <div key={index} className="kuika-skeleton-card-grid__card">
        <KuikaSkeleton width="var(--control-h-md)" height="var(--control-h-md)" />
        <KuikaSkeleton width="60%" height={TEXT_BAR_HEIGHT} />
        <KuikaSkeleton width="88%" height={SECOND_LINE_HEIGHT} />
        <KuikaSkeleton width={textBarWidth(index)} height={SECOND_LINE_HEIGHT} />
      </div>
    ))}
  </div>
);

export interface IKuikaSkeletonFormProps {
  fields?: number;
  className?: string;
}

/** Form fields: a label bar over a control-height field. */
export const KuikaSkeletonForm = ({ fields = 4, className }: IKuikaSkeletonFormProps): JSX.Element => (
  <div className={classNames("kuika-skeleton-form", className)} aria-hidden="true" data-testid="kuikaSkeletonForm">
    {indexes(fields).map((index: number): JSX.Element => (
      <div key={index} className="kuika-skeleton-form__field">
        <KuikaSkeleton width="30%" height={TEXT_BAR_HEIGHT} />
        <KuikaSkeleton height="var(--control-h-md)" />
      </div>
    ))}
  </div>
);

export interface IKuikaSkeletonDetailProps {
  /** Key / value rows under the title. */
  rows?: number;
  className?: string;
}

/** A detail panel: a title, then key / value rows. */
export const KuikaSkeletonDetail = ({ rows = 5, className }: IKuikaSkeletonDetailProps): JSX.Element => (
  <div className={classNames("kuika-skeleton-detail", className)} aria-hidden="true" data-testid="kuikaSkeletonDetail">
    <KuikaSkeleton width="40%" height="var(--control-h-sm)" />
    {indexes(rows).map((index: number): JSX.Element => (
      <div key={index} className="kuika-skeleton-detail__row">
        <KuikaSkeleton width="30%" height={TEXT_BAR_HEIGHT} />
        <KuikaSkeleton width={textBarWidth(index)} height={TEXT_BAR_HEIGHT} />
      </div>
    ))}
  </div>
);

export interface IKuikaSkeletonStatsProps {
  count?: number;
  className?: string;
}

/** Stat tiles: a caption bar over a value bar. */
export const KuikaSkeletonStats = ({ count = 4, className }: IKuikaSkeletonStatsProps): JSX.Element => (
  <div className={classNames("kuika-skeleton-stats", className)} aria-hidden="true" data-testid="kuikaSkeletonStats">
    {indexes(count).map((index: number): JSX.Element => (
      <div key={index} className="kuika-skeleton-stats__tile">
        <KuikaSkeleton width="50%" height={TEXT_BAR_HEIGHT} />
        <KuikaSkeleton width="40%" height="var(--control-h-md)" />
      </div>
    ))}
  </div>
);

export interface IKuikaSkeletonMenuProps {
  rows?: number;
  withIcon?: boolean;
  className?: string;
}

/** Rows of an opening dropdown or menu, at the compact row height. */
export const KuikaSkeletonMenu = ({ rows = 4, withIcon = false, className }: IKuikaSkeletonMenuProps): JSX.Element => (
  <div className={classNames("kuika-skeleton-menu", className)} aria-hidden="true" data-testid="kuikaSkeletonMenu">
    {indexes(rows).map((index: number): JSX.Element => (
      <div key={index} className="kuika-skeleton-menu__row">
        {withIcon && <KuikaSkeleton width="var(--control-icon-sm)" height="var(--control-icon-sm)" />}
        <KuikaSkeleton width={textBarWidth(index)} height={TEXT_BAR_HEIGHT} />
      </div>
    ))}
  </div>
);

export default KuikaSkeleton;
